import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getRace, getClass } from '@character-creator/data';
import { calculateModifier } from '@character-creator/core';
import type { StoredCharacter } from '../../utils/characterDatabase';
import { useApplyCharacterDraft } from '../../hooks/useApplyCharacterDraft';
import { usePdfExport } from '../../hooks/usePdfExport';
import { downloadCharacterJson } from '../../utils/characterExport';
import { CharacterContextMenu } from './CharacterContextMenu';

interface CharacterCardProps {
  character: StoredCharacter;
  rulesetId: string;
  onDelete: () => Promise<boolean>;
  onDuplicate: () => Promise<StoredCharacter | null>;
  onRename: (newName: string) => Promise<StoredCharacter | null>;
}

/**
 * Class colors for the avatar gradient.
 */
const CLASS_COLORS: Record<string, string> = {
  barbarian: '#e7623e',
  bard: '#ab6dac',
  cleric: '#91a1b2',
  druid: '#7a853b',
  fighter: '#7f513e',
  monk: '#51a5c5',
  paladin: '#b59e54',
  ranger: '#507f62',
  rogue: '#555752',
  sorcerer: '#992e2e',
  warlock: '#7b469b',
  wizard: '#2a50a1',
};

/**
 * Format a timestamp as relative time (e.g., "2h ago", "3d ago").
 */
function formatRelativeTime(isoString: string): string {
  const date = new Date(isoString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}

export function CharacterCard({
  character,
  rulesetId,
  onDelete,
  onDuplicate,
  onRename,
}: CharacterCardProps) {
  const navigate = useNavigate();
  const { applyDraft } = useApplyCharacterDraft();
  const { exportPdf } = usePdfExport();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isRenaming, setIsRenaming] = useState(false);
  const [renameValue, setRenameValue] = useState(character.name);
  const [isDeleting, setIsDeleting] = useState(false);
  const deleteTimeoutRef = useRef<number>();

  // Clean up timeout on unmount
  useEffect(() => {
    return () => {
      if (deleteTimeoutRef.current) {
        clearTimeout(deleteTimeoutRef.current);
      }
    };
  }, []);

  // Get race and class info
  const race = character.summary.raceId
    ? getRace(character.summary.raceId)
    : null;
  const charClass = character.summary.classId
    ? getClass(character.summary.classId)
    : null;

  // Get subclass info (from first class level)
  const firstClassLevel = character.draft.classLevels?.[0];
  const subclass = firstClassLevel?.subclassId && charClass
    ? charClass.subclasses?.find(sc => sc.id === firstClassLevel.subclassId)
    : null;

  const classColor = charClass
    ? CLASS_COLORS[charClass.id] || '#6b7280'
    : '#6b7280';

  const initial = character.name ? character.name[0].toUpperCase() : '?';

  // Calculate combat stats
  const abilityScores = character.draft.abilityScores;
  const conMod = calculateModifier(abilityScores.constitution);
  const dexMod = calculateModifier(abilityScores.dexterity);

  // Calculate HP (simplified for card display)
  // Formula: Level 1 = hit die max + CON mod, subsequent levels = average hit die + CON mod
  const level = character.summary.level;
  // Parse hit die string (e.g., 'd8' -> 8) or default to 10
  const hitDieValue = charClass?.hitDie ? parseInt(charClass.hitDie.slice(1), 10) : 10;
  const hp = hitDieValue + conMod + (level > 1 ? (level - 1) * (Math.floor(hitDieValue / 2) + 1 + conMod) : 0);

  // Calculate AC (base 10 + DEX mod, no armor for card display)
  const ac = 10 + dexMod;

  // Get speed from race (default 30)
  const speed = race?.speed ?? 30;

  // Calculate ability modifiers for display
  const abilityMods = {
    str: calculateModifier(abilityScores.strength),
    dex: dexMod,
    con: conMod,
    int: calculateModifier(abilityScores.intelligence),
    wis: calculateModifier(abilityScores.wisdom),
    cha: calculateModifier(abilityScores.charisma),
  };

  // Format modifier with sign
  const formatMod = (mod: number) => (mod >= 0 ? `+${mod}` : `${mod}`);

  // Handle opening/editing the character
  const handleOpen = () => {
    // Load the draft into the creation store and navigate
    applyDraft(character.draft, rulesetId);
    navigate(`/${rulesetId}/create`, { state: { fromLibrary: true } });
  };

  // Handle playing the character
  const handlePlay = () => {
    applyDraft(character.draft, rulesetId);
    navigate(`/${rulesetId}/play`);
  };

  // Handle rename
  const handleRenameSubmit = async () => {
    if (renameValue.trim() && renameValue !== character.name) {
      await onRename(renameValue.trim());
    }
    setIsRenaming(false);
  };

  // Handle delete with confirmation
  const handleDelete = async () => {
    if (isDeleting) {
      if (deleteTimeoutRef.current) {
        clearTimeout(deleteTimeoutRef.current);
      }
      await onDelete();
      setIsDeleting(false);
    } else {
      setIsDeleting(true);
      // Auto-cancel after 3 seconds
      deleteTimeoutRef.current = window.setTimeout(() => setIsDeleting(false), 3000);
    }
  };

  // Handle export JSON
  const handleExportJson = () => {
    downloadCharacterJson(character.draft, character.runtimeState);
    setIsMenuOpen(false);
  };

  // Handle export PDF
  const handleExportPdf = async () => {
    setIsMenuOpen(false);
    // Apply the draft to load it into the store, then export
    applyDraft(character.draft, rulesetId);
    await exportPdf({ filename: character.name });
  };

  return (
    <div className="group relative rounded-lg border border-slate-700 bg-slate-800/50 transition-all hover:border-slate-600 hover:bg-slate-800">
      {/* Card Content */}
      <div className="p-4">
        {/* Avatar and Info */}
        <div className="flex items-start gap-4">
          {/* Avatar */}
          <div
            className="flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-lg text-xl font-bold text-white"
            style={{
              background: `linear-gradient(135deg, ${classColor} 0%, ${classColor}99 100%)`,
            }}
          >
            {initial}
          </div>

          {/* Character Info */}
          <div className="min-w-0 flex-1">
            {isRenaming ? (
              <input
                type="text"
                value={renameValue}
                onChange={(e) => setRenameValue(e.target.value)}
                onBlur={handleRenameSubmit}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleRenameSubmit();
                  if (e.key === 'Escape') {
                    setRenameValue(character.name);
                    setIsRenaming(false);
                  }
                }}
                className="w-full rounded border border-amber-500 bg-slate-900 px-2 py-1 text-lg font-semibold text-white focus:outline-none focus:ring-2 focus:ring-amber-500"
                autoFocus
              />
            ) : (
              <h3 className="truncate text-lg font-semibold text-white">
                {character.name || 'Unnamed Character'}
              </h3>
            )}

            <p className="text-sm text-slate-400">
              Level {character.summary.level}{' '}
              {race?.name || 'Unknown'}{' '}
              {charClass?.name || 'Unknown'}
              {subclass && ` (${subclass.name})`}
            </p>

            {/* Combat Stats */}
            <div className="mt-1 flex gap-3 text-xs">
              <span className="text-red-400" title="Hit Points">
                <HeartIcon className="inline h-3 w-3 mr-0.5" />
                {hp}
              </span>
              <span className="text-blue-400" title="Armor Class">
                <ShieldIcon className="inline h-3 w-3 mr-0.5" />
                {ac}
              </span>
              <span className="text-green-400" title="Speed">
                {speed}ft
              </span>
            </div>

            {/* Ability Modifiers */}
            <div className="mt-1 flex gap-1.5 text-[10px] text-slate-500">
              <span title="Strength">STR {formatMod(abilityMods.str)}</span>
              <span title="Dexterity">DEX {formatMod(abilityMods.dex)}</span>
              <span title="Constitution">CON {formatMod(abilityMods.con)}</span>
              <span title="Intelligence">INT {formatMod(abilityMods.int)}</span>
              <span title="Wisdom">WIS {formatMod(abilityMods.wis)}</span>
              <span title="Charisma">CHA {formatMod(abilityMods.cha)}</span>
            </div>

            <p className="mt-1 text-xs text-slate-500">
              Updated {formatRelativeTime(character.updatedAt)}
            </p>
          </div>

          {/* Menu Button */}
          <div className="relative">
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="rounded p-1 text-slate-400 hover:bg-slate-700 hover:text-white"
              aria-label="Character options"
            >
              <svg
                className="h-5 w-5"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
              </svg>
            </button>

            {isMenuOpen && (
              <CharacterContextMenu
                onClose={() => setIsMenuOpen(false)}
                onOpen={handleOpen}
                onDuplicate={async () => {
                  await onDuplicate();
                  setIsMenuOpen(false);
                }}
                onRename={() => {
                  setIsRenaming(true);
                  setIsMenuOpen(false);
                }}
                onExportJson={handleExportJson}
                onExportPdf={handleExportPdf}
                onDelete={handleDelete}
                isDeleting={isDeleting}
              />
            )}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="mt-4 flex gap-2">
          <button
            onClick={handlePlay}
            className="flex-1 rounded-lg bg-amber-600 px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-amber-700"
          >
            Play
          </button>
          <button
            onClick={handleOpen}
            className="flex-1 rounded-lg border border-slate-600 bg-slate-700 px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-slate-600"
          >
            Edit
          </button>
        </div>
      </div>
    </div>
  );
}

function HeartIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="currentColor" viewBox="0 0 20 20">
      <path
        fillRule="evenodd"
        d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z"
        clipRule="evenodd"
      />
    </svg>
  );
}

function ShieldIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="currentColor" viewBox="0 0 20 20">
      <path
        fillRule="evenodd"
        d="M10 1.944A11.954 11.954 0 012.166 5C2.056 5.649 2 6.319 2 7c0 5.225 3.34 9.67 8 11.317C14.66 16.67 18 12.225 18 7c0-.682-.057-1.35-.166-2A11.954 11.954 0 0110 1.944z"
        clipRule="evenodd"
      />
    </svg>
  );
}
